// Browser tab title badge — shows "(N) Chat" when there are unread
// messages, plain "Chat" otherwise. WhatsApp/Slack pattern: lets the user
// see they have new activity in another tab without switching to it.
//
// Implementation runs as a React hook mounted once at the app root. It
// subscribes to the conversations slice and recomputes the unread sum on
// every change. Updates document.title in place.

import { useEffect } from 'react';
import { useChat } from '../store';

const BASE_TITLE = 'Chat';
// Cap the displayed count so a workspace-wide drama spree doesn't yield a
// "(847) Chat" tab title that pushes other content out of the favicon row.
const MAX_DISPLAY = 99;

function formatTitle(unread: number): string {
  if (unread <= 0) return BASE_TITLE;
  const display = unread > MAX_DISPLAY ? `${MAX_DISPLAY}+` : String(unread);
  return `(${display}) ${BASE_TITLE}`;
}

export function useTabTitleBadge(): void {
  // Sum of all unread counts across conversations the user is in. Hidden
  // / deleted convs naturally fall out of the list so we don't need to
  // filter here. The selector returns a number so React will re-run the
  // effect only when it actually changes.
  const totalUnread = useChat((s) =>
    s.conversations.reduce((acc, c) => acc + (c.unreadCount ?? 0), 0),
  );

  useEffect(() => {
    document.title = formatTitle(totalUnread);
  }, [totalUnread]);

  // On unmount (e.g. logout) restore the plain title so the SPA doesn't
  // leave a stale "(3) Chat" sitting in the tab.
  useEffect(() => {
    return () => {
      document.title = BASE_TITLE;
    };
  }, []);
}
